import { Close } from "@/components/Icons/utility/Close.tsx";
import Modal from "@/components/Modal/Modal.tsx";
import { PopoverContentProps } from "@/components/Popover/PopoverContentProps.ts";
import { RateRowProps } from "@/components/RateRow/RateRowProps.ts";
import { RateRowDetails } from "@/components/RateRowDetails/RateRowDetails.tsx";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { formatDiscount } from "@/helpers/formatDiscount.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { useGetCancelationPolicyTranslated } from "@/helpers/translation/useGetCancelationPolicyTranslated.ts";
import { useGetRPTranslated } from "@/helpers/translation/useGetRPTranslated.ts";
import { RateSellMode } from "@/helpers/types/types.ts";
import DOMPurify from "dompurify";
import { Ban, Check, FileText, ShoppingCart } from "lucide-react";
import { KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowContainer, Popover } from "react-tiny-popover";

export const RateRow = ({
  ratePlan,
  isSelected,
  reservationNights,
  onSelect,
  disabled = false,
  showRadio = true,
  isLast = false,
  isInCart = false,
  isPaymentIncompatible = false,
  mode = "picker",
  showFullDescription = false,
}: RateRowProps) => {
  const { t } = useTranslation();
  const { containerEl } = useShadowRoot();
  const { name, description } = useGetRPTranslated(ratePlan);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [incompatiblePopoverOpen, setIncompatiblePopoverOpen] = useState(false);

  const { name: policyName, description: policyDescription } = useGetCancelationPolicyTranslated(
    ratePlan?.cancelationPolicy,
  );

  const hasDescription = !!description;
  const hasCancelationPolicy = !!ratePlan?.cancelationPolicy?.id;

  const priceRP =
    ratePlan.rateSellMode !== RateSellMode.PerPerson
      ? ratePlan
      : ratePlan.childRatePlans?.find((child) => child.occupancy === 2) || ratePlan;
  const price = priceRP.defaultPriceBreakdown?.withDiscount;
  const discountPct = priceRP.defaultPriceBreakdown?.discountPercentage ?? 0;
  const hasDiscount = discountPct > 0;

  const occupancyText = ratePlan.rateGroup?.guestCapacity === 1 ? `1 ${t("person")}` : `2 ${t("persons")}`;

  const isFeatured = ratePlan.isFeatured;
  const isSwitcher = mode === "switcher";
  const isEffectivelyDisabled = disabled || isPaymentIncompatible;

  const handleRowClick = () => {
    if (isEffectivelyDisabled) return;
    if (isSwitcher) onSelect();
  };

  const handleRowKeyDown = (e: KeyboardEvent) => {
    if (isEffectivelyDisabled) return;
    if (isSwitcher && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      className={`c-rate-row-wrapper${isLast ? " is-last" : ""}${hasDescription ? " is-expanded" : ""}${
        isSelected ? " is-selected" : ""
      }${isInCart ? " is-in-cart" : ""}${isFeatured ? " is-featured" : ""}${
        isPaymentIncompatible ? " is-incompatible" : ""
      }`}
    >
      {isFeatured && <span className="c-rate-row__featured-tag">{t("featured")}</span>}
      <div
        className={`c-rate-row${isEffectivelyDisabled ? " is-disabled" : ""}${
          isSwitcher ? " is-switchable" : ""
        }`}
        onClick={handleRowClick}
        role={isSwitcher ? "button" : undefined}
        tabIndex={isSwitcher ? 0 : undefined}
        onKeyDown={isSwitcher ? handleRowKeyDown : undefined}
      >
        {isInCart && (
          <span className="c-rate-row__cart-check">
            <Check size={14} strokeWidth={2.5} />
          </span>
        )}
        {showRadio && (
          <span className="c-rate-row__radio">
            <span className="c-rate-row__radio-outer">
              {isSelected && <span className="c-rate-row__radio-inner" />}
            </span>
          </span>
        )}
        <span className="c-rate-row__content">
          <span className="c-rate-row__left">
            <span className="c-rate-row__name">{name}</span>
            {isSwitcher && isPaymentIncompatible && (
              <Popover
                isOpen={incompatiblePopoverOpen}
                parentElement={containerEl}
                positions={["top", "left", "bottom"]}
                containerStyle={{ zIndex: "600" }}
                content={({ position, childRect, popoverRect }: PopoverContentProps) => (
                  <ArrowContainer
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor=""
                    arrowSize={10}
                    className="popoverArrow"
                  >
                    <div className="popoverContent">{t("incompatiblePopover")}</div>
                  </ArrowContainer>
                )}
              >
                <span
                  className="c-rate-row__incompatible-label"
                  onMouseEnter={() => setIncompatiblePopoverOpen(true)}
                  onMouseLeave={() => setIncompatiblePopoverOpen(false)}
                >
                  <Ban size={12} strokeWidth={2} />
                  {t("incompatiblePaymentConfig")}
                </span>
              </Popover>
            )}
            <span className="c-rate-row__meta">
              {hasCancelationPolicy && (
                <button
                  type="button"
                  className="c-rate-row__policy"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPolicyOpen(true);
                  }}
                >
                  <FileText size={12} strokeWidth={2} />
                  <span>{t("generalTermsAndConditions")}</span>
                </button>
              )}
              {isSwitcher && hasDescription && (
                <button
                  type="button"
                  className="c-rate-row__details-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailsOpen((prev) => !prev);
                  }}
                >
                  {detailsOpen ? t("collapse") : t("details")}
                </button>
              )}
            </span>
          </span>
          <span className="c-rate-row__right">
            {price != null && (
              <span className="c-rate-row__price">
                {hasDiscount && (
                  <span className="c-rate-row__discount-row">
                    <span className="c-rate-row__discount-badge">-{formatDiscount(discountPct)}</span>
                    <span className="c-rate-row__price-original">
                      {formatCurrency(priceRP.defaultPriceBreakdown?.withoutDiscount)}
                    </span>
                  </span>
                )}
                <span className="c-rate-row__price-row">
                  {reservationNights > 1 && <span className="c-rate-row__price-from">{t("from")}</span>}
                  <strong className={hasDiscount ? "c-rate-row__price-discounted" : ""}>
                    {formatCurrency(price)}
                  </strong>
                </span>
              </span>
            )}
            {reservationNights > 0 && (
              <span className="c-rate-row__info">
                {`${occupancyText}, ${reservationNights} ${reservationNights > 1 ? t("nights") : t("night")}`}
              </span>
            )}
          </span>
        </span>
        {!isSwitcher && isPaymentIncompatible && (
          <Popover
            isOpen={incompatiblePopoverOpen}
            parentElement={containerEl}
            positions={["top", "left", "bottom"]}
            containerStyle={{ zIndex: "600", pointerEvents: "none" }}
            content={({ position, childRect, popoverRect }: PopoverContentProps) => (
              <ArrowContainer
                position={position}
                childRect={childRect}
                popoverRect={popoverRect}
                arrowColor=""
                arrowSize={10}
                className="popoverArrow"
              >
                <div className="popoverContent">{t("incompatiblePopover")}</div>
              </ArrowContainer>
            )}
          >
            <span
              onMouseEnter={() => setIncompatiblePopoverOpen(true)}
              onMouseLeave={() => setIncompatiblePopoverOpen(false)}
            >
              <button
                type="button"
                className="c-be-button--small c-be-button c-rate-row__select-btn c-rate-row__select-btn--incompatible"
                disabled
              >
                <span className="c-rate-row__select-text">{t("incompatiblePaymentConfig")}</span>
                <span className="c-rate-row__select-icon">
                  <Ban size={16} strokeWidth={2} />
                </span>
              </button>
            </span>
          </Popover>
        )}
        {!isSwitcher && !isPaymentIncompatible && (
          <button
            type="button"
            className="c-be-button--small c-be-button c-rate-row__select-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!isEffectivelyDisabled) onSelect();
            }}
            disabled={isSelected || isEffectivelyDisabled}
          >
            {isSelected ? (
              <Check size={16} strokeWidth={2.5} />
            ) : (
              <>
                <span className="c-rate-row__select-text">{isInCart ? t("addOneMore") : t("select")}</span>
                <span className="c-rate-row__select-icon">
                  <ShoppingCart size={16} strokeWidth={2} />
                </span>
              </>
            )}
          </button>
        )}
      </div>
      {hasDescription && (!isSwitcher || detailsOpen) && (
        <div className="c-rate-row__expand">
          <div className="c-rate-row__expand-inner">
            <RateRowDetails
              ratePlan={ratePlan}
              showFullDescription={isSwitcher ? true : showFullDescription}
            />
          </div>
        </div>
      )}
      {hasCancelationPolicy && (
        <Modal isOpen={policyOpen} onClose={() => setPolicyOpen(false)}>
          <div className="c-policy-modal">
            <div className="c-policy-modal__close">
              <button className="c-close-button" onClick={() => setPolicyOpen(false)}>
                <Close />
              </button>
            </div>
            <div className="c-policy-modal__header">
              <h5 className="u-h5 u-fw-600">{policyName || t("generalTermsAndConditions")}</h5>
            </div>
            {policyDescription && (
              <div
                className="c-policy-modal__content u-rte u-rte--document"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policyDescription) }}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};
